import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../../components/SEO";

const featureCards = [
  {
    id: "01",
    title: "Precision Print Infrastructure",
    description:
      "Servo-controlled flexo lines, calibrated color systems, and sharp repeatability keep every run stable from the first label to the last.",
  },
  {
    id: "02",
    title: "Quality That Scales",
    description:
      "Multi-stage inspection, material validation, and batch-level checks help us maintain export-ready consistency on high-volume jobs.",
  },
  {
    id: "03",
    title: "Fast Commercial Turnaround",
    description:
      "Tight production planning and responsive execution let us support both routine dispatch cycles and urgent launch requirements.",
  },
  {
    id: "04",
    title: "Material Expertise",
    description:
      "From BOPP and paper to specialty constructions, we recommend substrates based on shelf-life, finish, and application conditions.",
  },
  {
    id: "05",
    title: "Design-to-Print Support",
    description:
      "We refine artwork for print feasibility, finishing alignment, and visual clarity so premium branding survives actual production.",
  },
  {
    id: "06",
    title: "Reliable Partnership Model",
    description:
      "Clear communication, transparent quoting, and dependable delivery make us easy to work with beyond the first order.",
  },
];

const highlights = [
  { value: "4+", label: "Years of production experience" },
  { value: "500+", label: "Brands supported across segments" },
  { value: "99.9%", label: "QC pass rate on dispatch" },
  { value: "5-7", label: "Days average lead time" },
];

const proofPoints = [
  {
    title: "0.1mm Registration Control",
    desc: "Servo-driven micro adjustments prevent design bleeding and alignment shifts."
  },
  {
    title: "Advanced Tape Testing",
    desc: "Standardized cross-hatch ink adhesion checks on synthetic BOPP & paper."
  },
  {
    title: "Static Elimination",
    desc: "Active neutralization bars ensure smooth automatic labeling application."
  },
  {
    title: "Express Logistics",
    desc: "Direct shipping routes to NCR, South India, and West India hubs."
  }
];

const manufacturingMetrics = [
  { value: "0.1 mm", label: "Registration accuracy on repeat jobs" },
  { value: "8 Color", label: "Multi-color flexo capability" },
  { value: "24-48h", label: "Express production for urgent requirements" },
];

const workflow = [
  {
    step: "Discover",
    detail:
      "We study your product, substrate, quantity, and application environment before suggesting the print route.",
  },
  {
    step: "Engineer",
    detail:
      "Artwork, color targets, material selection, and finishing details are tuned for actual factory performance.",
  },
  {
    step: "Deliver",
    detail:
      "Production and QC move in lockstep so dispatch stays fast without compromising visual or adhesive integrity.",
  },
];

const WhyChooseUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Why Choose Sahajink | Leading Label Experts in India"
        description="Discover why top brands choose Sahajink for their label printing needs. 4+ years of expertise, 99.9% QC pass rate, and fast turnaround for Pharma and FMCG."
        keywords="best label printing surat, industrial label quality, precision printing india, label supplier advantages, why sahajink"
      />
      <main className="min-h-screen overflow-x-hidden bg-[#0a1628] text-white">
        <section className="relative overflow-hidden pt-[140px] pb-20 md:pt-[180px] md:pb-24 lg:pt-[210px] lg:pb-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.28),transparent_32%),radial-gradient(circle_at_85%_18%,rgba(96,165,250,0.16),transparent_24%),linear-gradient(180deg,#0a1628_0%,#091120_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:52px_52px] opacity-60" />

          <div className="relative z-10 mx-auto max-w-[1200px] px-6">
            <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-[760px]"
              >
                <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-2 text-[12px] sm:text-sm font-semibold text-blue-200 backdrop-blur-md">
                  <span className="h-2 w-2 rounded-full bg-blue-400 shadow-[0_0_14px_rgba(96,165,250,0.9)]" />
                  Premium manufacturing advantage
                </div>

                <h1 className="mb-6 text-[36px] sm:text-[42px] font-extrabold leading-[1.3] text-white md:text-[58px] lg:text-[65px]">
                  Why brands stay with
                  <span className="bg-gradient-to-r from-blue-300 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                    Sahajink.
                  </span>
                </h1>

                <p className="max-w-[680px] text-[14px] sm:text-base leading-relaxed sm:leading-8 text-white/70 md:text-lg">
                  Sahajink combines precision printing, dependable quality
                  control, and application-driven material expertise to deliver
                  label solutions that perform consistently in demanding
                  industries.
                </p>

                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/#contact"
                    className="rounded-full bg-blue-600 px-7 py-3 text-[12px] sm:text-sm font-bold uppercase tracking-[0.18em] text-white transition duration-300 hover:scale-[1.02] hover:bg-blue-500"
                  >
                    Request Quote
                  </Link>
                  <Link
                    to="/#products"
                    className="rounded-full border border-white/20 bg-white/5 px-7 py-3 text-[12px] sm:text-sm font-semibold uppercase tracking-[0.18em] text-white transition duration-300 hover:border-blue-300/40 hover:bg-white/10"
                  >
                    View Products
                  </Link>
                </div>
              </motion.div>

              <div className="grid gap-4 sm:grid-cols-2">
                {highlights.map((item, idx) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                    className="rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-[0_24px_80px_rgba(3,8,20,0.35)] backdrop-blur-xl"
                  >
                    <div className="mb-2 text-3xl font-extrabold text-white md:text-4xl">
                      {item.value}
                    </div>
                    <p className="text-sm leading-6 text-white/65">
                      {item.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-20 md:py-24">
          <div className="mx-auto max-w-[1200px] px-6">
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                style={{
                  transform: "translate3d(0,0,0)",
                  willChange: "transform, opacity",
                }}
              >
                <div className="mb-4 inline-flex rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-blue-700">
                  Core Differentiators
                </div>

                <h2 className="text-3xl font-extrabold text-slate-900 md:text-5xl">
                  The premium layer behind every label.
                </h2>
              </motion.div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {featureCards.map((feature, idx) => (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  style={{
                    transform: "translate3d(0,0,0)",
                    willChange: "transform, opacity",
                  }}
                >
                  {/* STATIC CARD (design same) */}
                  <article className="group relative h-full flex flex-col overflow-hidden rounded-[30px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fbff)] p-8 transition duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_28px_90px_rgba(37,99,235,0.12)]">
                    <div className="absolute right-6 top-7 sm:top-5 text-6xl font-black text-slate-100 transition duration-300 group-hover:text-blue-100">
                      {feature.id}
                    </div>

                    <div className="relative z-10 flex h-full flex-col">
                      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-blue-200 bg-blue-50 text-blue-700">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-5 w-5 fill-none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 12.5 9.2 17 19 7"
                          />
                        </svg>
                      </div>

                      <h3 className="mb-4 text-2xl font-bold text-slate-900">
                        {feature.title}
                      </h3>

                      <p className="text-sm leading-7 text-slate-600 md:text-base">
                        {feature.description}
                      </p>

                      <div className="mt-8 h-1 w-14 rounded-full bg-blue-400/35 transition-all duration-300 group-hover:w-24 group-hover:bg-blue-300" />
                    </div>
                  </article>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden py-20 md:py-24 lg:py-28">
          <div className="absolute left-0 top-12 h-52 w-52 rounded-full bg-blue-500/10 blur-[110px]" />
          <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-cyan-400/10 blur-[130px]" />

          <div className="relative z-10 mx-auto max-w-[1200px] px-6">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-white/70">
                  Manufacturing Confidence
                </div>
                <h2 className="mb-6 text-3xl font-extrabold tracking-tight text-white md:text-5xl">
                  Built for brands that cannot afford print inconsistency.
                </h2>
                <p className="mb-8 max-w-[560px] text-sm leading-7 text-white/65 md:text-base">
                  Our production workflow is designed for repeatable print
                  quality, material stability, and dependable dispatch timelines.
                  From regulated applications to premium retail packaging, every
                  order is managed with process discipline and commercial
                  responsiveness.
                </p>

                <div className="grid gap-4 sm:grid-cols-2">
                  {proofPoints.map((point) => (
                    <div
                      key={point.title}
                      className="rounded-[24px] border border-white/10 bg-white/5 px-5 py-5 transition-all duration-300 hover:border-blue-500/30 hover:bg-white/[0.07]"
                    >
                      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/15 text-blue-200">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4 fill-none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h4 className="text-sm font-bold text-white mb-1">{point.title}</h4>
                      <p className="text-xs text-white/50 leading-relaxed">{point.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              <div className="grid gap-5 sm:grid-cols-3 lg:grid-cols-1">
                {manufacturingMetrics.map((item, idx) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-7 shadow-[0_24px_80px_rgba(3,8,20,0.3)]"
                  >
                    <div className="mb-3 text-3xl font-extrabold text-white md:text-4xl">
                      {item.value}
                    </div>
                    <p className="text-sm leading-7 text-white/65">
                      {item.label}
                    </p>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="rounded-[28px] border border-blue-400/20 bg-blue-500/10 p-7"
                >
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-200 mb-4">
                    Multi-Stage Inspection Protocol
                  </p>
                  <div className="space-y-4">
                    {[
                      "Raw Material Verification (Face stock caliper & adhesive coat weight checking)",
                      "Real-Time Video Web Inspection (Automated defect scan at 100m/min)",
                      "Mandatory Reel Rewinding Scan (Manual validation of barcode scan rates)"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-blue-200">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-3 w-3 fill-none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <p className="text-xs sm:text-sm leading-relaxed text-white/80">{item}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#eef4ff_100%)] py-20 md:py-24">
          <div className="mx-auto max-w-[1200px] px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12 max-w-[690px]"
            >
              <div className="mb-4 inline-flex rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-blue-700">
                How We Work
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 md:text-[48px] leading-[1.2]">
                A sharper process creates a better client experience.
              </h2>
            </motion.div>

            <div className="grid gap-6 lg:grid-cols-3">
              {workflow.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="rounded-[30px] border border-white bg-white p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <span className="text-sm font-bold uppercase tracking-[0.24em] text-blue-700">
                      0{index + 1}
                    </span>
                    <span className="ml-4 h-px flex-1 bg-slate-200" />
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-slate-900">
                    {item.step}
                  </h3>
                  <p className="text-sm leading-7 text-slate-600 md:text-base">
                    {item.detail}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden py-20 md:py-24 lg:py-28">
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500/60 to-transparent" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mx-auto max-w-[980px] px-6 text-center"
          >
            <div className="mb-5 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-white/70">
              Ready To Start
            </div>
            <h2 className="mx-auto mb-6 max-w-[820px] text-3xl font-extrabold tracking-tight text-white md:text-6xl">
              Quality you can trust. Labels you can order with confidence.
            </h2>
            <p className="mx-auto mb-10 max-w-[680px] text-sm leading-relaxed sm:leading-8 text-white/65 md:text-lg">
              At Sahajink, we work like an extension of your team. From material
              selection and print planning to final dispatch, we help you move
              faster, maintain consistency, and deliver packaging your customers
              can trust.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/#contact"
                className="rounded-full bg-white px-8 py-4 text-[12px] sm:text-sm font-bold uppercase tracking-[0.18em] text-[#0a1628] transition duration-300 hover:scale-[1.02] hover:bg-blue-300"
              >
                Start Your Project
              </Link>
              <Link
                to="/"
                className="rounded-full border border-white/15 bg-white/5 px-8 py-4 text-[12px] sm:text-sm font-semibold uppercase tracking-[0.18em] text-white transition duration-300 hover:bg-white/10"
              >
                Back To Home
              </Link>
            </div>
          </motion.div>
        </section>
      </main>
    </>
  );
};

export default WhyChooseUs;
